/**
 * Tool Search Metrics
 *
 * Tracks metrics related to tool search usage and performance.
 * Used for monitoring, optimization, and debugging.
 *
 * Console output is routed through the shared `@wtc/logger` package logger so
 * every entry inherits baseline JSON context. The exported function signatures
 * are kept stable so callers are unaffected.
 */

import { logger } from '../../logger';

export interface ToolSearchMetrics {
  /** Timestamp of the metric */
  timestamp: Date;

  /** Chat ID or request ID */
  requestId: string;

  /** Tools discovered in this request */
  toolsDiscovered: string[];

  /** Number of tools discovered */
  discoveredCount: number;

  /** Search queries executed */
  searchQueries: string[];

  /** Tool search variant used (regex or bm25) */
  variant: 'regex' | 'bm25';

  /** Whether tool search was enabled */
  toolSearchEnabled: boolean;

  /** Estimated context tokens saved */
  contextTokensSaved: number;

  /** Total context tokens used */
  totalContextTokens: number;

  /** Tools executed in this request */
  toolsExecuted: string[];

  /** Tool execution success rate (0-1) */
  executionSuccessRate: number;

  /** Any errors encountered */
  errors: string[];

  /** Duration of the request in milliseconds */
  durationMs: number;
}

/**
 * In-memory metrics storage (for development/testing)
 * In production, this should be persisted to a database
 */
const metricsStore: ToolSearchMetrics[] = [];

/**
 * Record a tool search metric
 */
export function recordToolSearchMetric(metric: ToolSearchMetrics): void {
  metricsStore.push(metric);

  // Keep only last 1000 metrics in memory
  if (metricsStore.length > 1000) {
    metricsStore.shift();
  }

  logger.debug('tool search metrics', {
    op: 'recordToolSearchMetric',
    requestId: metric.requestId,
    discovered: metric.toolsDiscovered,
    variant: metric.variant,
    tokensSaved: metric.contextTokensSaved,
    durationMs: metric.durationMs,
  });
}

/**
 * Get all recorded metrics
 */
export function getToolSearchMetrics(): ToolSearchMetrics[] {
  return [...metricsStore];
}

/**
 * Get metrics for a specific request
 */
export function getMetricsForRequest(requestId: string): ToolSearchMetrics | undefined {
  return metricsStore.find((m) => m.requestId === requestId);
}

/**
 * Get aggregated metrics
 */
export function getAggregatedMetrics(
  timeWindowMs: number = 3600000 // 1 hour
): {
  totalRequests: number;
  avgToolsDiscovered: number;
  avgContextTokensSaved: number;
  avgExecutionSuccessRate: number;
  mostDiscoveredTools: Array<{ tool: string; count: number }>;
  errorRate: number;
} {
  const now = Date.now();
  const recentMetrics = metricsStore.filter((m) => now - m.timestamp.getTime() < timeWindowMs);

  if (recentMetrics.length === 0) {
    return {
      totalRequests: 0,
      avgToolsDiscovered: 0,
      avgContextTokensSaved: 0,
      avgExecutionSuccessRate: 0,
      mostDiscoveredTools: [],
      errorRate: 0,
    };
  }

  // Calculate averages
  const avgToolsDiscovered =
    recentMetrics.reduce((sum, m) => sum + m.discoveredCount, 0) / recentMetrics.length;

  const avgContextTokensSaved =
    recentMetrics.reduce((sum, m) => sum + m.contextTokensSaved, 0) / recentMetrics.length;

  const avgExecutionSuccessRate =
    recentMetrics.reduce((sum, m) => sum + m.executionSuccessRate, 0) / recentMetrics.length;

  // Count tool discoveries
  const toolCounts = new Map<string, number>();
  for (const metric of recentMetrics) {
    for (const tool of metric.toolsDiscovered) {
      toolCounts.set(tool, (toolCounts.get(tool) || 0) + 1);
    }
  }

  const mostDiscoveredTools = Array.from(toolCounts.entries())
    .map(([tool, count]) => ({ tool, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Calculate error rate
  const errorCount = recentMetrics.filter((m) => m.errors.length > 0).length;
  const errorRate = errorCount / recentMetrics.length;

  return {
    totalRequests: recentMetrics.length,
    avgToolsDiscovered,
    avgContextTokensSaved,
    avgExecutionSuccessRate,
    mostDiscoveredTools,
    errorRate,
  };
}

/**
 * Clear all metrics (for testing)
 */
export function clearMetrics(): void {
  metricsStore.length = 0;
}
